// SQL statement splitter that respects quotes and comments.
//
// Returns an array of { text, start, end } where start/end are character
// offsets into the source — useful for "run statement under cursor".
//
// Empty statements (e.g. ";;" or trailing whitespace+;) are skipped, as are
// comment-only segments (a stray `-- note`, `# note`, or `/* … */`) — neither is
// a runnable statement, and MySQL rejects a comment-only query as "empty". An
// executable comment (`/*!40014 SET … */`, `/*M!… */`) is NOT comment-only: the
// server runs its body, so it's kept as a statement.

const SINGLE = "'";
const DOUBLE = '"';
const BACKTICK = '`';

// splitStatements splits on `;` (quote/comment aware).
export function splitStatements(sql) { return scanStatements(sql, false); }

// splitScript additionally honours `DELIMITER x` directives, so stored-procedure
// / trigger / function bodies (whose internal `;` would otherwise split the
// statement) can be created. A `DELIMITER $$` line changes the terminator to
// `$$` until another `DELIMITER ;` resets it; the DELIMITER lines themselves are
// stripped (they're a client directive, never sent to the server). With no
// DELIMITER directives it's identical to splitStatements.
export function splitScript(sql) { return scanStatements(sql, true); }

// Sticky: matched in place at a line start (no slice of the rest of the source).
const DELIM_RE = /delimiter[ \t]+(\S+)[ \t]*\r?(?:\n|$)/iy;

// Core scanner shared by both. Returns { text, start, end } per statement;
// start/end are offsets into the source (used by statementAtCursor). When
// honorDelimiter is set, `DELIMITER x` lines retune the terminator. withStop
// adds `stop` — the offset just past the statement's terminator — for
// statementAtCursor only (the public splitters' shape is unchanged).
function scanStatements(sql, honorDelimiter, withStop = false) {
  const out = [];
  let startIdx = 0;
  let delimiter = ';';
  let inSingle = false, inDouble = false, inBacktick = false;
  let inLineComment = false, inBlockComment = false;

  for (let i = 0; i < sql.length; i++) {
    const c  = sql[i];
    const nx = sql[i + 1];

    if (inLineComment) { if (c === '\n') inLineComment = false; continue; }
    if (inBlockComment) {
      if (c === '*' && nx === '/') { inBlockComment = false; i++; }
      continue;
    }
    if (inSingle) {
      if (c === '\\' && nx) { i++; continue; }
      if (c === SINGLE) inSingle = false;
      continue;
    }
    if (inDouble) {
      if (c === '\\' && nx) { i++; continue; }
      if (c === DOUBLE) inDouble = false;
      continue;
    }
    if (inBacktick) {
      if (c === BACKTICK) inBacktick = false;
      continue;
    }

    // DELIMITER directive — only at a line start, with no pending statement
    // (only whitespace/comments since the last terminator, e.g. a header
    // comment line). Gated on the line beginning with d/D so the regex stays
    // off the hot path for ordinary lines.
    if (honorDelimiter && (c === 'd' || c === 'D') && (i === 0 || sql[i - 1] === '\n')) {
      DELIM_RE.lastIndex = i;
      const m = DELIM_RE.exec(sql);
      if (m && !hasExecutableSql(sql.slice(startIdx, i))) {
        delimiter = m[1];
        const nl = sql.indexOf('\n', i);
        i = nl === -1 ? sql.length : nl; // loop i++ steps past the newline
        startIdx = i + 1;
        continue;
      }
    }

    if (c === '-' && nx === '-' && dashComment(sql, i)) { inLineComment  = true; i++; continue; }
    if (c === '#')               { inLineComment  = true; continue; } // MySQL `#` comment
    if (c === '/' && nx === '*') { inBlockComment = true; i++; continue; }
    if (c === SINGLE)             { inSingle   = true; continue; }
    if (c === DOUBLE)             { inDouble   = true; continue; }
    if (c === BACKTICK)           { inBacktick = true; continue; }

    if (sql.startsWith(delimiter, i)) {
      pushIfNonEmpty(out, sql, startIdx, i, withStop ? i + delimiter.length : -1);
      i += delimiter.length - 1; // loop i++ steps past the rest of the delimiter
      startIdx = i + 1;
    }
  }
  pushIfNonEmpty(out, sql, startIdx, sql.length, withStop ? sql.length : -1);
  return out;
}

function pushIfNonEmpty(out, sql, start, end, stop) {
  const raw  = sql.slice(start, end);
  const trim = raw.trim();
  if (trim === '') return;             // empty (e.g. a lone `;` or whitespace)
  if (!hasExecutableSql(trim)) return; // comment-only (`-- …`, `# …`, `/* … */`)
  out.push(stop >= 0 ? { text: trim, start, end, stop } : { text: trim, start, end });
}

// True if `text` has any SQL token outside of comments and whitespace. A segment
// that is nothing but comments — a stray `-- note`, `# note`, or `/* … */`, often
// a trailing comment after the last real statement — must NOT be sent to the
// server: MySQL answers it with "Query was empty". Quote/comment-aware so a `#`
// or `--` *inside* a string literal still counts as real SQL. An executable
// comment (`/*!…*/`, MariaDB `/*M!…*/`) is real SQL: the server runs its body.
function hasExecutableSql(text) {
  let inSingle = false, inDouble = false, inBacktick = false;
  let inLineComment = false, inBlockComment = false;
  for (let i = 0; i < text.length; i++) {
    const c  = text[i];
    const nx = text[i + 1];
    if (inLineComment)  { if (c === '\n') inLineComment = false; continue; }
    if (inBlockComment) { if (c === '*' && nx === '/') { inBlockComment = false; i++; } continue; }
    if (inSingle)   { if (c === '\\' && nx) { i++; continue; } if (c === SINGLE)   inSingle   = false; continue; }
    if (inDouble)   { if (c === '\\' && nx) { i++; continue; } if (c === DOUBLE)   inDouble   = false; continue; }
    if (inBacktick) { if (c === BACKTICK) inBacktick = false; continue; }
    if (c === '-' && nx === '-' && dashComment(text, i)) { inLineComment  = true; i++; continue; }
    if (c === '#')               { inLineComment  = true; continue; }
    if (c === '/' && nx === '*') {
      if (text[i + 2] === '!' || (text[i + 2] === 'M' && text[i + 3] === '!')) return true;
      inBlockComment = true; i++; continue;
    }
    if (c === SINGLE)   { inSingle   = true; continue; }
    if (c === DOUBLE)   { inDouble   = true; continue; }
    if (c === BACKTICK) { inBacktick = true; continue; }
    if (!/\s/.test(c)) return true; // a real token, outside any comment
  }
  return false;
}

// Returns the statement whose range contains the cursor, or null. A cursor
// sitting on or just after the trailing terminator (`;`, or `$$` under
// DELIMITER) is treated as inside the preceding statement (matches DBeaver /
// Navicat behaviour). DELIMITER-aware like splitScript, so a cursor inside a
// `DELIMITER $$ … CREATE PROCEDURE … END$$` body yields the whole CREATE, never
// one inner statement. A cursor in a gap no statement covers (a DELIMITER line,
// a comment-only segment) takes the next statement, else the last one.
export function statementAtCursor(sql, cursor) {
  const stmts = scanStatements(sql, true, true);
  for (const s of stmts) {
    if (cursor >= s.start && cursor <= s.stop) return strip(s);
  }
  const next = stmts.find((s) => s.start > cursor);
  if (next) return strip(next);
  // After the last terminator: pick the last statement if any.
  return stmts.length > 0 ? strip(stmts[stmts.length - 1]) : null;
}

const strip = ({ text, start, end }) => ({ text, start, end });

// MySQL starts a `--` comment only when the second dash is followed by
// whitespace or a control character (or the end of input): `5--1` is
// 5 - (-1), not 5 then a comment that would swallow the rest of the line.
function dashComment(src, i) {
  const after = src.charCodeAt(i + 2);
  return Number.isNaN(after) || after <= 32;
}
